import { create } from 'zustand';

/**
 * Quiet background-conversation notification state (design §8.4).
 *
 * Keyed by conversationId. A `done` flag drives the completion badge; a
 * `needsApproval` flag drives the DISTINCT "needs you" badge for a background
 * run parked on a tool-approval interrupt. The flags are set ONLY for
 * conversations that are NOT currently focused (the focused conversation
 * renders completion / approval inline). Focusing a conversation clears it.
 *
 * Holds ONLY booleans — never transcripts. The Vercel SDK `Chat` (kept warm by
 * the registry) remains the single source of truth for messages; this store
 * is a thin notification side-channel (design §8.2 — no second buffer).
 */
export interface ConversationNotification {
  /** Run completed while this conversation was unfocused. */
  done: boolean;
  /** Run parked on a tool-approval interrupt while unfocused — needs the user. */
  needsApproval: boolean;
}

interface AssistantNotificationsState {
  byConversation: Record<string, ConversationNotification>;
  /** Mark a conversation's run complete (quiet completion badge). */
  markDone: (conversationId: string) => void;
  /** Mark a conversation as awaiting tool approval (distinct "needs you" badge). */
  markNeedsApproval: (conversationId: string) => void;
  /** Clear all notifications for a conversation (called on focus). */
  clear: (conversationId: string) => void;
}

const EMPTY: ConversationNotification = { done: false, needsApproval: false };

export const useAssistantNotificationsStore = create<AssistantNotificationsState>((set) => ({
  byConversation: {},
  markDone: (conversationId) =>
    set((s) => ({
      byConversation: {
        ...s.byConversation,
        [conversationId]: { ...(s.byConversation[conversationId] ?? EMPTY), done: true },
      },
    })),
  markNeedsApproval: (conversationId) =>
    set((s) => ({
      byConversation: {
        ...s.byConversation,
        [conversationId]: { ...(s.byConversation[conversationId] ?? EMPTY), needsApproval: true },
      },
    })),
  clear: (conversationId) =>
    set((s) => {
      if (!(conversationId in s.byConversation)) return s;
      const next = { ...s.byConversation };
      delete next[conversationId];
      return { byConversation: next };
    }),
}));
